import React, { useState, useRef, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useSnackbar } from "notistack";
import { Form, Button, Container, Row, Col, Spinner, InputGroup } from "react-bootstrap";
import { Eye, EyeSlash } from "react-bootstrap-icons";
import { useAuthStore } from "../model";
import { regenerateCode, register, verify } from "../api";
import PhoneInput from "react-phone-input-2";
import { getUserByToken } from "@/entities/user/api";
import { PasswordInputGroup, VerificationCodeInput } from "@/shared/ui";
import { useMediaQuery } from "react-responsive";

export const RegisterForm = () => {
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [numberPhone, setNumberPhone] = useState("");
  const [password, setPassword] = useState("");
  const [repeatPassword, setRepeatPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showRepeatPassword, setShowRepeatPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [validated, setValidated] = useState(false);
  const [showVerification, setShowVerification] = useState(false);
  const isMobile = useMediaQuery({ maxWidth: 768 })
  const [isAgreed, setIsAgreed] = useState(false);

  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const { setUser, setIsAuthorization } = useAuthStore();

  const buttonRef = useRef<HTMLButtonElement>(null);
  const [timer, setTimer] = useState(60);

  useEffect(() => {
    let interval: NodeJS.Timeout;
    if (showVerification && timer > 0) {
      interval = setInterval(() => setTimer((prev) => prev - 1), 1000);
    }
    return () => clearInterval(interval);
  }, [showVerification, timer]);

  const isValidPhone = (phone: string) => {
    const digits = phone.replace(/\D/g, "");
    return digits.length === 11 && (digits.startsWith("7") || digits.startsWith("8"));
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setValidated(true);

    if (!firstName || !lastName || !numberPhone || !password || !repeatPassword) {
      enqueueSnackbar("Пожалуйста, заполните все поля.", { variant: "error" });
      buttonRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
      return;
    }

    if (!isValidPhone(numberPhone)) {
      enqueueSnackbar("Введите корректный номер телефона.", { variant: "error" });
      buttonRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
      return;
    }

    if (password !== repeatPassword) {
      enqueueSnackbar("Пароли не совпадают.", { variant: "error" });
      buttonRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
      return;
    }

    setLoading(true);
    try {
      const digits = numberPhone.replace(/\D/g, "").replace(/^7/, "8");

      const deviceToken = (window as any).fcmDeviceToken || null;
      const deviceTokenLoc = localStorage.getItem('fcmDeviceToken');

      const data = await register({ firstName, lastName, phone: digits, password, ...((deviceToken || deviceTokenLoc) && { deviceToken: deviceToken || deviceTokenLoc })});

      if (data.success) {
        setShowVerification(true);
        setTimer(60);
        enqueueSnackbar("Введите код подтверждения.", { variant: "info" });
      }
    } catch (err) {
      console.log(err);
    } finally {
      setLoading(false);
    }
  };

  const handleVerify = async (e: React.FormEvent<HTMLFormElement>, code: string) => {
    e.preventDefault();

    if (code.length !== 4) {
      enqueueSnackbar("Введите полный 4-значный код.", { variant: "error" });
      return;
    }

    const digits = numberPhone.replace(/\D/g, "").replace(/^7/, "8");

    try {
      setLoading(true);
      const data = await verify(digits, +code);

      if (data.success) {
        localStorage.setItem("token", data.result);
        setIsAuthorization();
        navigate("/");
        enqueueSnackbar("Регистрация успешна!", { variant: "success" });

        const me = await getUserByToken(data.result);
        if (me) setUser(me.result);
      } else {
        enqueueSnackbar(data.error || "Неверный код подтверждения", { variant: "error" });
      }
    } catch (err) {
      console.error("Verification error:", err);
      enqueueSnackbar("Ошибка при подтверждении кода", { variant: "error" });
    } finally {
      setLoading(false);
    }
  };

    const handleResendCode = () => {
        const phone = numberPhone.replace(/\D/g, "").replace(/^7/, "8");
  
        regenerateCode(phone).then(data => {
          if (data.success) {
            enqueueSnackbar("Код запрошен повтрно!", { variant: "info" });
            setTimer(60)
          }
        })
    };
    

  const roundedClass = isMobile ? "rounded-0" : "rounded-4";

  return (
    <Container className="p-0 d-flex align-items-center justify-content-center" style={{ height: "calc(100% - 42px)" }}>
      <div
        className={`${roundedClass} overflow-x-hidden overflow-y-auto shadow-lg`}
        style={{ width: "100%", maxHeight: isMobile ? "100%" : 600, height: "100%", maxWidth: "900px" }}
      >
        <Row className={`g-0 h-${isMobile ? 25 : 100}`} >
          <Col
            xs={12}
            md={6}
            className="d-flex pt-3 flex-column align-items-center justify-content-center text-white"
            style={{
              height: "100%",
              backgroundColor: "#121218",
              minHeight: isMobile ? "220px" : "470px",
            }}
          >
            <img
              src="/logo.webp"
              alt="O!-Chat Logo"
              style={{
                height: `${isMobile ? 120 : 190}px`,
                width: "auto",
                marginBottom: isMobile ? ".2rem" : ".4rem",
                objectFit: "contain",
              }}
            />
            <h1 className="o-chat-gradient-text text-center">
              {showVerification ?
                "Верификация"
                :
                <span style={{ lineHeight: isMobile ? "" : "70px" }} >
                  Регистрация&nbsp;
                  {
                    !isMobile && <br />
                  }
                  в O!-Chat
                </span>
              }
            </h1>
          </Col>

          <Col
            xs={12}
            md={6}
            className="d-flex align-items-center justify-content-center"
            style={{ backgroundColor: "#f9fafc", color: "#111" }}
          >
            <div
              className="w-100"
              style={{
                maxWidth: "100%",
                padding: "2rem 1.5rem",
                // maxHeight: isMobile ? "calc(100vh - 4rem)" : "none",
                overflow: "hidden",
              }}
            >
              {showVerification ? (
                <VerificationCodeInput
                  onVerify={handleVerify}
                  onBack={() => {
                    setShowVerification(false);
                    setValidated(false);
                  }}
                  backTitle="Назад к регистрации"
                  loading={loading}
                  timer={timer}
                  onResend={handleResendCode}
                />
              ) : (
                <Form noValidate validated={validated} onSubmit={handleSubmit}>
                  <Form.Group controlId="formFirstName" className="mb-3">
                    <Form.Label>Имя</Form.Label>
                    <Form.Control
                      required
                      type="text"
                      placeholder="Введите имя"
                      className="rounded-5"
                      value={firstName}
                      onChange={(e) => setFirstName(e.target.value)}
                      isInvalid={validated && !firstName}
                    />
                    <Form.Control.Feedback type="invalid">
                      Обязательное поле
                    </Form.Control.Feedback>
                  </Form.Group>

                  <Form.Group controlId="formLastName" className="mb-3">
                    <Form.Label>Фамилия</Form.Label>
                    <Form.Control
                      required
                      type="text"
                      placeholder="Введите фамилию"
                      className="rounded-5"
                      value={lastName}
                      onChange={(e) => setLastName(e.target.value)}
                      isInvalid={validated && !lastName}
                    />
                    <Form.Control.Feedback type="invalid">
                      Обязательное поле
                    </Form.Control.Feedback>
                  </Form.Group>

                  <Form.Group controlId="formPhone" className="mb-3">
                    <Form.Label>Номер телефона</Form.Label>
                    <PhoneInput
                      country={"ru"}
                      disableDropdown={true}
                      specialLabel=""
                      masks={{ ru: "(...) ...-..-.." }}
                      onlyCountries={["ru"]}
                      placeholder="Введите номер телефона"
                      value={numberPhone}
                      onChange={(phone) => {
                        if (phone.startsWith("8")) {
                          setNumberPhone(`7${phone.slice(1)}`);
                        } else {
                          setNumberPhone(phone);
                        }
                      }}
                      inputClass={`form-control rounded-5 w-100 ${validated && !isValidPhone(numberPhone) ? "is-invalid" : ""
                        }`}
                    />
                    {validated && !isValidPhone(numberPhone) && (
                      <div className="text-danger" style={{ fontSize: "0.875em" }}>
                        Введите корректный номер телефона.
                      </div>
                    )}
                  </Form.Group>

                  <PasswordInputGroup
                    label="Пароль"
                    value={password}
                    name="password"
                    onChange={(e) => setPassword(e.target.value)}
                    showPassword={showPassword}
                    toggleShow={() => setShowPassword(!showPassword)}
                    placeholder="Введите пароль"
                    isInvalid={validated && !password}
                    feedback="Обязательное поле"
                  />

                  <PasswordInputGroup
                    label="Повторите пароль"
                    value={repeatPassword}
                    name="repeatPassword"
                    onChange={(e) => setRepeatPassword(e.target.value)}
                    showPassword={showRepeatPassword}
                    toggleShow={() => setShowRepeatPassword(!showRepeatPassword)}
                    placeholder="Повторите пароль"
                    isInvalid={validated && (repeatPassword !== password || !repeatPassword)}
                    feedback="Пароли не совпадают"
                  />

                  <div className="d-flex" >
                    <input
                      type="checkbox"
                      id="agree"
                      checked={isAgreed}
                      onChange={(e) => setIsAgreed(e.target.checked)}
                      required
                      style={{ marginTop: '4px' }}
                    />
                    <label htmlFor="agree" style={{ marginLeft: '12px', fontSize: 12 }}>
                      Я согласен с{' '}
                      <Link to="/info" style={{ color: "#495057" }}>Политикой конфиденциальности</Link>{' '}
                      и{' '}
                      <Link to="/info" style={{ color: "#495057" }}>Условиями использования</Link>
                    </label>
                  </div>

                  <Button
                    ref={buttonRef}
                    variant="outline-secondary"
                    type="submit"
                    className="w-100 mt-3"
                    disabled={loading || !isAgreed}
                  >
                    {loading ? (
                      <>
                        <Spinner
                          as="span"
                          animation="border"
                          size="sm"
                          role="status"
                          aria-hidden="true"
                          className="me-2"
                        />
                        Регистрация...
                      </>
                    ) : (
                      "Зарегистрироваться"
                    )}
                  </Button>
                </Form>
              )}
            </div>
          </Col>
        </Row>
      </div>
    </Container>
  );
};
